"use client";

/* eslint-disable no-nested-ternary */
import { RelatedContentSection } from "@/types/dto/destinations-page/destination-page-types";
import React, { useState } from "react";
import { Box } from "@mui/material";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import { chunkArray, interleave } from "@/utils/general-util";
import { useTranslations } from "next-intl";
import { SectionHeader } from "@/components/common/section-header";
import Grid from "@mui/material/Grid";
import RelatedBlogCard from "./related-content-blog-card";
import RelatedVideoCard from "./related-content-video-card";

interface RelatedContentSectionProps {
  relatedContentSection: RelatedContentSection;
  moduleRankingPosition: number;
  centerHeader?: boolean;
}

const RelatedContentSectionLayout: React.FC<RelatedContentSectionProps> = ({ relatedContentSection, moduleRankingPosition, centerHeader }) => {
  const [visibleRows, setVisibleRows] = useState(1);
  const blogPosts = relatedContentSection.relatedContentItems.filter(item => item.contentType === "blogPost");
  const wistiaVideos = relatedContentSection.relatedContentItems.filter(item => item.contentType === "wistia");
  const interleaved = interleave(blogPosts, wistiaVideos);
  const rows = chunkArray(interleaved, 2);


  const handleShowMore = () => {
    setVisibleRows((prev) => Math.min(prev + 1, rows.length));
  };

  const { basicInfo } = relatedContentSection;
  const t = useTranslations("DESTINATIONS");
  return (
    <Box 
      className="fluid-box anchor-section" 
      id="related-content"
      data-track="visibility"
      data-parent-container-type="content"
      data-module-name="Related Content Section"
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition}
      component="section"
      sx={{
        mb: 3,
        backgroundColor: "background.warm_strong"
      }}
    >
      <Box className="destination-page-container" data-testid="related-content-container"
        sx={{
          padding: {
            zero: "4rem 2.4rem",
            legacyMobile: "4rem 2.4rem",
            legacyTablet: "6rem clamp(2.4rem, 4.5vw, 6.5rem)"
          }
        }}
      >
        <SectionHeader
          headerText={basicInfo.headerText}
          copyText={basicInfo.copyText}
          headerTestId="related-content-header"
          copyTestId="related-content-copy"
          headerSx={centerHeader ? { textAlign: "center" } : undefined}
          copySx={centerHeader ? { textAlign: "center" } : undefined}
        />
        <Grid container spacing={2} sx={{ marginTop: "4rem" }}>
          {rows.slice(0, visibleRows).flat().map((item, index) => (
            <Grid size={{ lg: 6, md: 12, xs: 12 }} data-testid={`related-content-section-item-${index}`} key={item.id}>
              {item.contentType === "blogPost" ? (
                <RelatedBlogCard item={item} index={index} />
              ) : item.contentType === "wistia" ? (
                <RelatedVideoCard item={item} index={index} />
              ) : null}
            </Grid>
          ))}
        </Grid>
        {visibleRows < rows.length && (
          <div style={{ textAlign: "center", margin: "2rem 0 2rem 0" }}>
            <ColletteButton
              onClick={handleShowMore}
              variant="outlined"
              size="large"
              sx={{
                backgroundColor: "background.default"
              }}
              label={t("See More")}
              testId="data-related-content-show-more-button"
              data-track="click"
            />
          </div>
        )}
      </Box>
    </Box>
  );
};

export default RelatedContentSectionLayout;
